import { Link, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Eye, Download, Quote, ChevronRight, AlertTriangle } from "lucide-react";

export function PaperCard({ paper }) {
  const navigate = useNavigate();
  const year = paper.published_at ? new Date(paper.published_at).getFullYear() : null;
  let meta = "";
  if (paper.volume) meta += `Vol. ${paper.volume}`;
  if (year) meta += `${meta ? " " : ""}(${year})`;
  if (paper.pages) meta += `${meta ? ", " : ""}pp. ${paper.pages}`;
  const pubDate = paper.published_at
    ? new Date(paper.published_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : null;
  return (
    <article
      data-testid={`paper-card-${paper.id}`}
      onClick={() => navigate(`/paper/${paper.id}`)}
      className="group cursor-pointer bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-5 hover:shadow-md hover:border-primary/30 hover:-translate-y-0.5 transition-all duration-200"
    >
      <div className="flex items-center gap-2 mb-2.5 flex-wrap">
        <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800 text-[11px]">
          {paper.article_type}
        </Badge>
        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 text-[11px]">
          Open Access
        </Badge>
        {paper.retracted && (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 text-[11px] gap-1">
            <AlertTriangle className="w-3 h-3" /> Retracted
          </Badge>
        )}
      </div>

      <Link to={`/paper/${paper.id}`} data-testid={`paper-link-${paper.id}`}>
        <h3 className="font-serif font-bold text-lg leading-snug text-slate-900 dark:text-slate-100 group-hover:text-primary dark:group-hover:text-sky-300 transition-colors">
          {paper.title}
        </h3>
      </Link>

      <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 line-clamp-2">{paper.abstract}</p>

      <p className="text-sm font-bold text-primary dark:text-sky-400 mt-3">
        {paper.authors?.map((a) => a.name).slice(0, 4).join(", ")}
        {paper.authors?.length > 4 ? " et al." : ""}
      </p>

      <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap text-sm">
          {paper.journal && (
            <Link onClick={(e) => e.stopPropagation()} to={`/journal/${paper.journal.slug}`} className="font-bold text-primary dark:text-sky-400 hover:underline">
              {paper.journal.title}
            </Link>
          )}
          {meta && <span className="text-slate-500 dark:text-slate-400">· {meta}</span>}
        </div>
        {pubDate && <span className="text-sm font-bold text-primary dark:text-sky-400">{pubDate}</span>}
      </div>

      <div className="mt-2 flex items-center gap-4 text-xs font-bold text-emerald-600 dark:text-emerald-400">
        <span className="inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {paper.views || 0}</span>
        <span className="inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" /> {paper.downloads || 0}</span>
        <span className="inline-flex items-center gap-1"><Quote className="w-3.5 h-3.5" /> {paper.citations || 0}</span>
      </div>

      {paper.doi && (
        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
          <span className="font-mono text-xs text-slate-400">DOI: {paper.doi}</span>
          <Link to={`/paper/${paper.id}`} className="text-xs font-medium text-amber-600 inline-flex items-center gap-0.5 group-hover:gap-1.5 transition-all">
            Read <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}
    </article>
  );
}
